import { API } from 'aws-amplify';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';

import { formatAddress, formImageForEvent, imageForEvent } from 'api/eventPresentation';
import {
    fetchFundraisingEventDetails,
    fetchFundraisingEvents,
    placeFundraisingHold,
} from 'api/fundraisingAPI';
import { CharityEvent, PlacedHold, RegistrationRequest } from 'api/types';
import { CHARITY_API_NAME } from 'services/constants';
import { FUNDRAISING_API_BASE_URL } from 'services/env';
import { useHandleError } from 'services/errors';

export type { CharityEvent, CharityEventPackage, PlacedHold } from 'api/types';

export interface CharityEventResponse {
    charity_event_id: string;
    event_name: string;
    event_description: string;
    event_start: string;
    location: string;
    address: {
        address_line_1: string;
        address_line_2: string;
        city: string;
        state: string;
        zipcode: string;
    };
    registration_start: string;
    registration_end: string;
    packages: {
        package_id: string;
        package_name: string;
        package_price: number;
        package_description: string;
        spots_remaining: number;
        max_package_count_per_user: number;
    }[];
}

export const fetchCharityEvents = async () =>
    API.get(CHARITY_API_NAME, '', {}) as Promise<CharityEventResponse[]>;

export const mapCharityEvent = (event: CharityEventResponse): CharityEvent => ({
    eventId: event.charity_event_id,
    eventTitle: event.event_name,
    eventDate: event.event_start,
    description: event.event_description,
    image: imageForEvent(event.event_name),
    location: event.location,
    address: formatAddress(event.address),
    registrationStart: event.registration_start,
    registrationEnd: event.registration_end,
    formImage: formImageForEvent(event.event_name),
    packages: event.packages.map((eventPackage) => ({
        packageId: eventPackage.package_id,
        title: eventPackage.package_name,
        name: eventPackage.package_name,
        cost: eventPackage.package_price,
        description: eventPackage.package_description,
        remaining: eventPackage.spots_remaining,
        maxPackageCount: eventPackage.max_package_count_per_user,
    })),
});

// One environment variable decides which backend serves reads. Unsetting
// VITE_FUNDRAISING_API_BASE_URL rolls the site back to the legacy API without a
// code deploy — the reversibility Phase 2 is built around.
const loadEvents = async (): Promise<CharityEvent[]> => {
    if (FUNDRAISING_API_BASE_URL) {
        return fetchFundraisingEvents();
    }
    return (await fetchCharityEvents()).map(mapCharityEvent);
};

export const useCharityEvents = () => {
    const [events, setEvents] = useState<CharityEvent[]>();
    const handleError = useHandleError();
    useEffect(() => {
        (async () => {
            try {
                const loaded = await loadEvents();
                setEvents(loaded.sort((a, b) => dayjs(b.eventDate).diff(a.eventDate)));
            } catch (err) {
                handleError('Failed to retrieve event information.', err as Error);
            }
        })();
    }, [handleError]);
    return events;
};

export const fetchCharityEventDetails = async (eventId: string) =>
    API.get(CHARITY_API_NAME, `/${eventId}`, {}) as Promise<CharityEventResponse>;

const loadEventDetails = async (eventId: string): Promise<CharityEvent> => {
    if (FUNDRAISING_API_BASE_URL) {
        return fetchFundraisingEventDetails(eventId);
    }
    return mapCharityEvent(await fetchCharityEventDetails(eventId));
};

export const useCharityEventDetails = (eventId?: string) => {
    const [event, setEvent] = useState<CharityEvent>();
    const handleError = useHandleError();
    useEffect(() => {
        (async () => {
            try {
                if (eventId) {
                    setEvent(await loadEventDetails(eventId));
                }
            } catch (err) {
                handleError('Failed to retrieve event information.', err as Error);
            }
        })();
    }, [eventId, handleError]);
    return event;
};

const initiateLegacyRegistration = async (
    registration: RegistrationRequest,
): Promise<PlacedHold> => {
    const response = (await API.post(CHARITY_API_NAME, '/register', {
        body: {
            event_id: registration.eventId,
            registered_packages: registration.packages.map((pkg) => ({
                package_id: pkg.packageId,
                qty: pkg.qty,
                details: pkg.details,
            })),
        },
    })) as { client_secret: string };
    return { clientSecret: response.client_secret };
};

/**
 * Start a registration, against whichever backend is configured.
 *
 * The same env-var switch that routes reads routes this: unsetting
 * VITE_FUNDRAISING_API_BASE_URL sends registration back to the legacy API with
 * no code deploy, which is the rollback Phase 4 is built around.
 */
export const initiateEventRegistration = async (
    registration: RegistrationRequest,
): Promise<PlacedHold> =>
    FUNDRAISING_API_BASE_URL
        ? placeFundraisingHold(registration)
        : initiateLegacyRegistration(registration);
